// src/components/Help.jsx
//
// New page -- SideMenu.jsx's "Help" nav item has pointed at /help since
// it was written, but the route/page never existed until now (that gap
// was called out explicitly in the original restyle plan). Matches
// CET-HTML-Code/help.html: a "Get in Touch" info panel on the left
// (phone/email/hours) and a real "Send Us a Message" form on the right
// that POSTs to /help-messages and gets saved to the new Help_Messages
// table (see help_and_notifications_tables.sql -- run that once against
// CostDB before this form will actually save anything).
//
// FIX: the "Get in Touch" panel used to be hardcoded text right in this
// file. Per request, it now loads from GET /contact-info (Contact_Info
// table -- see contact_info_table.sql, run that once against CostDB
// before this panel has anything to show) so the numbers/emails/hours
// can be changed without a code deploy. Read-only for now: no edit
// screen exists yet to write back to that table.
import React, { useEffect, useState } from "react";
import { api as axios } from "../config/apiConfig";

export default function Help() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [contactInfo, setContactInfo] = useState(null);
  const [contactInfoError, setContactInfoError] = useState("");

  useEffect(() => {
    axios
      .get("/contact-info")
      .then((res) => {
        if (res.data?.success) {
          setContactInfo(res.data.data);
        } else {
          setContactInfoError(res.data?.message || "Could not load contact info.");
        }
      })
      .catch((err) => {
        setContactInfoError(
          err.response?.data?.message ||
            "Could not load contact info. Make sure contact_info_table.sql has been run and the backend was restarted."
        );
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!fullName.trim() || !email.trim() || !message.trim()) {
      setError("Full name, email, and message are required.");
      return;
    }

    setSending(true);
    try {
      const res = await axios.post("/help-messages", {
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        subject: subject.trim(),
        message: message.trim()
      });
      setSuccess(res.data?.message || "Your message has been sent.");
      setFullName("");
      setEmail("");
      setPhone("");
      setSubject("");
      setMessage("");
    } catch (err) {
      // Distinguishes the two very different failure modes that both
      // used to show the same generic text: a real HTTP error back from
      // the server (err.response exists -- show its status code and, if
      // the server sent one, its actual message) vs. the request never
      // reaching the server at all (err.response is undefined -- wrong
      // port, backend not running/not restarted since /help-messages
      // was added, CORS, etc.). Makes it possible to tell which one is
      // happening from the message alone instead of needing devtools.
      if (err.response) {
        setError(
          err.response.data?.message ||
            `Server error (HTTP ${err.response.status}). If this just started happening, the backend may need to be restarted after the /help-messages route was added.`
        );
      } else {
        setError(
          `Could not reach the server at all (${err.message || "network error"}). Make sure the backend is running and was restarted after the update, and that help_and_notifications_tables.sql has been run.`
        );
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="page-title-head d-flex align-items-center mb-3">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail ("Cost
              Estimation / {page}"), same pattern and color (#02bc9c,
              16px) as the Cost Estimation wizard's Step1-7 breadcrumbs
              -- replaces the earlier #788CA5 bordered/uppercase title
              treatment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              Help Center
            </span>
          </nav>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="card-body p-0">
          <div className="row g-0">
            {/* Left side: static contact info */}
            <div className="col-lg-5 col-xl-4 p-2 p-xl-5 text-start bg-transparent">
              <h3 className="fw-bold mb-2 text-dark">Get in Touch</h3>
              <p className="text-muted fs-14 mb-4 ms-0">
                Have a question or need estimation support? Reach out directly.
              </p>

              {contactInfoError && !contactInfo && (
                <div className="alert alert-warning py-2 fs-13" role="alert">
                  {contactInfoError}
                </div>
              )}

              {contactInfo && (
                <>
                  <div className="d-flex flex-column gap-4 align-items-start ms-0 ps-0">
                    {contactInfo.contactNumbers.length > 0 && (
                      <div className="d-flex gap-3 align-items-start">
                        <div className="avatar-lg flex-shrink-0">
                          <span className="avatar-title bg-light text-dark fs-20 p-3 rounded-circle">
                            <i className="fs-2 ti ti-phone-call" />
                          </span>
                        </div>
                        <div className="text-start">
                          <span className="d-block text-muted fs-13">Contact Numbers</span>
                          {contactInfo.contactNumbers.map((num) => (
                            <a key={num} className="d-block fw-semibold text-dark text-decoration-none" href={`tel:${num}`}>
                              {num}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {contactInfo.emailIds.length > 0 && (
                      <div className="d-flex gap-3 align-items-start">
                        <div className="avatar-lg flex-shrink-0">
                          <span className="avatar-title bg-light text-dark fs-20 p-3 rounded-circle">
                            <i className="fs-2 ti ti-mail" />
                          </span>
                        </div>
                        <div className="text-start">
                          <span className="d-block text-muted fs-13">Email ID</span>
                          {contactInfo.emailIds.map((addr) => (
                            <a key={addr} className="d-block fw-semibold text-dark text-decoration-none" href={`mailto:${addr}`}>
                              {addr}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {contactInfo.workingHours && (
                      <div className="d-flex gap-3 align-items-start">
                        <div className="avatar-lg flex-shrink-0">
                          <span className="avatar-title bg-light text-dark fs-20 p-3 rounded-circle">
                            <i className="fs-2 ti ti-clock" />
                          </span>
                        </div>
                        <div className="text-start">
                          <span className="d-block text-muted fs-13">Working Hours</span>
                          <span className="d-block fw-semibold text-dark">{contactInfo.workingHours}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {contactInfo.responseNote && (
                    <div className="border-top border-secondary-subtle mt-4 pt-3 text-start ms-0">
                      <small className="text-muted">{contactInfo.responseNote}</small>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right side: real contact form */}
            <div className="col-lg-7 col-xl-8 p-4 p-xl-5 bg-white">
              <div className="mb-4">
                <h4 className="fw-bold mb-1 text-dark">Send Us a Message</h4>
                <p className="text-muted fs-14 mb-0">Fill out the details below and our team will get back to you shortly.</p>
              </div>

              {error && (
                <div className="alert alert-danger py-2" role="alert">
                  {error}
                </div>
              )}
              {success && (
                <div className="alert alert-success py-2" role="alert">
                  {success}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-medium text-dark" htmlFor="help-name">
                      Full Name
                    </label>
                    <input
                      className="form-control py-2"
                      id="help-name"
                      type="text"
                      placeholder="Enter your full name"
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-medium text-dark" htmlFor="help-email">
                      Email Address
                    </label>
                    <input
                      className="form-control py-2"
                      id="help-email"
                      type="email"
                      placeholder="Enter your email address"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-medium text-dark" htmlFor="help-phone">
                      Phone Number
                    </label>
                    <input
                      className="form-control py-2"
                      id="help-phone"
                      type="tel"
                      placeholder="+966-1234567890"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-medium text-dark" htmlFor="help-subject">
                      Subject
                    </label>
                    <input
                      className="form-control py-2"
                      id="help-subject"
                      type="text"
                      placeholder="What's the issue you are facing?"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-medium text-dark" htmlFor="help-message">
                      Message
                    </label>
                    <textarea
                      className="form-control py-2"
                      id="help-message"
                      rows={4}
                      placeholder="Write your message here..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-12 text-end mt-4">
                    <button className="btn btn-primary px-4 py-2 rounded-pill fw-medium" type="submit" disabled={sending}>
                      {sending ? "Sending..." : "Send Message"} <i className="ti ti-send ms-1" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
